import { useQuery } from '@apollo/client/react';
import GET_API_KEYS_FOR_ACCOUNT from '../gql/api_keys/getApiKeysForAccount.gql';
import { Table } from '@repo/ui/Table';

import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';

import ApiKey from './AccountKeys/ApiKey';
import ApiKeysTableFilter from './ApiKeysTableFilter';
import { Form } from '@repo/ui/Form';

import { z } from 'zod';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Link } from 'react-router';
import { truncateName } from '@/lib/truncateName';

const API_KEYS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  name: z.string().default(''),
});

const PAGE_SIZE = 5;

export interface ApiKeysTableProps {
  accountId?: string;
}

const ApiKeysTable = ({ accountId }: ApiKeysTableProps) => {
  const user = useUser();

  const canUpdateApiKeys = POLICIES['api_keys:update'](user);

  const filter = useZodQuery({
    schema: API_KEYS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, ...queryFields } = filter.queryValues;

  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
      },
      coerceValue,
      ['name']
    ),
  ];

  const { loading, error, data } = useQuery(GET_API_KEYS_FOR_ACCOUNT, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    notifyOnNetworkStatusChange: true,
  });
  if (error) {
    console.error(error);
  }

  const { edges: apiKeysNodes, pageInfo } = data?.apiKeysForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className="mb-6">
        <ApiKeysTableFilter />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={apiKeysNodes?.map(({ node }) => node) ?? []}
        dataType="apiKeys"
        error={!!error}
        columns={[
          {
            title: 'Name',
            width: 'xl',
            Cell({ entry: { id, name } }) {
              const label = truncateName(name ?? '');
              // The key's page needs update access, so only link for those who have it
              return canUpdateApiKeys ? (
                <Link to={`/a/${accountId}/develop/${id}`} className="underline" title={name ?? ''}>
                  {label}
                </Link>
              ) : (
                <span title={name ?? ''}>{label}</span>
              );
            },
          },

          {
            title: 'Key',
            width: 'xxl',
            Cell({ entry: { token, id } }) {
              return <ApiKey token={token} id={id} />;
            },
          },
        ]}
      ></Table>
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default ApiKeysTable;
